import { BaseError } from 'make-error';
import { JsonValue } from '../Utility/JsonValue';
import { LocalCache } from './LocalCache';

type StoredContent = Blob | JsonValue;

/**
 * An error occurred when attempting to cache data.
 */
class LocalCacheStoreError extends BaseError {
    constructor(message: string) {
        super(message);
    }
}

/**
 * An error occurred when attempting to retrieve cached data.
 */
export class LocalCacheRetrieveError extends BaseError {
    constructor(message: string) {
        super(message);
    }
}

/**
 * Try to save the `content` for a given `url`.
 * @param url
 * @param content
 * @param cacheId - The key used to retrieve the cached data. (The `url` will be used if not set)
 * @returns The IndexedDb record id.
 * @throws {@link LocalCacheStoreError} if caching fails.
 */
export function localCacheStore(
    url: string,
    content: StoredContent,
    cacheId?: string
): Promise<number> {
    return new Promise((resolve, reject) => {
        LocalCache.save(url, content, cacheId)
            .done((record: number) => resolve(record))
            .fail((message: string) => reject(new LocalCacheStoreError(message)));
    });
}

/**
 * Try to get the `content` for a given `cacheId`.
 * @param cacheId
 * @returns
 * @throws {@link LocalCacheRetrieveError} if retrieval fails, or no `content` is cached.
 */
export function localCacheRetrieve(cacheId: string): Promise<StoredContent> {
    return new Promise((resolve, reject) => {
        LocalCache.get(cacheId)
            .done((content: StoredContent) => resolve(content))
            .fail((message: string) => reject(new LocalCacheRetrieveError(message)));
    });
}
